import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ReviewDetailPage from './pages/ReviewDetailPage';
import ReviewSelfReviewPage from './pages/ReviewSelfReviewPage';
import ReviewPeerReviewPage from './pages/ReviewPeerReviewPage';
import ReviewCyclesPage from './pages/ReviewCyclesPage';
import AllReviewsPage from './pages/AllReviewsPage';
import RoleTemplatesPage from './pages/RoleTemplatesPage';
import UserManagementPage from './pages/UserManagementPage';
import ReferenceDataPage from './pages/ReferenceDataPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import GovernancePage from './pages/GovernancePage';
import OutstandingTasksPage from './pages/OutstandingTasksPage';
import EmailLogsPage from './pages/EmailLogsPage';
import OrganogramPage from './pages/OrganogramPage';
import CheckInsPage from './pages/CheckInsPage';
import CheckInAuditLogPage from './pages/CheckInAuditLogPage';
import ReviewAuditLogPage from './pages/ReviewAuditLogPage';
import IdpAuditLogPage from './pages/IdpAuditLogPage';
import UserAuditLogPage from './pages/UserAuditLogPage';
import LeaveAuditLogPage from './pages/LeaveAuditLogPage';
import ImportedLeaveBalancesPage from './pages/ImportedLeaveBalancesPage';
import TeamRosterPage from './pages/TeamRosterPage';
import TeamReviewsPage from './pages/TeamReviewsPage';
import ReviewsToManagePage from './pages/ReviewsToManagePage';
import MyReviewsPage from './pages/MyReviewsPage';
import DisputeManagementPage from './pages/DisputeManagementPage';
import IndividualDevelopmentPlanPage from './pages/IndividualDevelopmentPlanPage';
import AllIdpsPage from './pages/AllIdpsPage';
import TeamLeavePage from './pages/TeamLeavePage';
import OrganisationLeavePage from './pages/OrganisationLeavePage';
import MyLeavePage from './pages/MyLeavePage';
import MyTasksPage from './pages/MyTasksPage';
import MyExpensesPage from './pages/MyExpensesPage';
import NewExpenseClaimPage from './pages/NewExpenseClaimPage';
import TeamExpensesPage from './pages/TeamExpensesPage';
import OrganisationExpensesPage from './pages/OrganisationExpensesPage';
import OrganisationWorkforcePage from './pages/OrganisationWorkforcePage';
import ExpenseClaimAuditLogPage from './pages/ExpenseClaimAuditLogPage';
import HighFivesPage from './pages/HighFivesPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdministratorRoute from './components/AdministratorRoute';
import ExpenseFeatureGate from './components/ExpenseFeatureGate';
import HighFivesFeatureGate from './components/HighFivesFeatureGate';
import { SuccessSnackbarProvider } from './contexts/SuccessSnackbarContext';
import SessionExpirySnackbar from './components/SessionExpirySnackbar';
import { AuthProvider } from './contexts/AuthContext';
import { AgentProvider } from './contexts/AgentContext';
import './App.css';

const muiTheme = createTheme({
  components: {
    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
  },
});

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MyTasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/:viewType?"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-tasks"
          element={
            <ProtectedRoute>
              <MyTasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews/to-manage"
          element={
            <ProtectedRoute>
              <ReviewsToManagePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews/:id/self-review"
          element={
            <ProtectedRoute>
              <ReviewSelfReviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews/:id/peer-review"
          element={
            <ProtectedRoute>
              <ReviewPeerReviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews/:id"
          element={
            <ProtectedRoute>
              <ReviewDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-reviews"
          element={
            <ProtectedRoute>
              <MyReviewsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/review-cycles/:cycleId/outstanding"
          element={
            <ProtectedRoute>
              <OutstandingTasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/review-cycles"
          element={
            <ProtectedRoute>
              <ReviewCyclesPage />
            </ProtectedRoute>
          }
        />
        <Route path="/review-cycle-settings" element={<Navigate to="/settings" replace />} />
        <Route
          path="/settings"
          element={
            <AdministratorRoute>
              <SettingsPage />
            </AdministratorRoute>
          }
        />
        <Route
          path="/reviews/all"
          element={
            <ProtectedRoute>
              <AllReviewsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/individual-development-plan/all"
          element={
            <ProtectedRoute>
              <AllIdpsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/role-templates"
          element={
            <ProtectedRoute>
              <RoleTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team-roster"
          element={
            <ProtectedRoute>
              <TeamRosterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team-reviews"
          element={
            <ProtectedRoute>
              <TeamReviewsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reference-data"
          element={
            <ProtectedRoute>
              <ReferenceDataPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/email-logs"
          element={
            <ProtectedRoute>
              <EmailLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/governance"
          element={
            <ProtectedRoute>
              <GovernancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organogram/:viewType"
          element={
            <ProtectedRoute>
              <OrganogramPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkins/audit-log"
          element={
            <ProtectedRoute>
              <CheckInAuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkins/:id"
          element={
            <ProtectedRoute>
              <CheckInsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkins"
          element={
            <ProtectedRoute>
              <CheckInsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews/audit-log"
          element={
            <ProtectedRoute>
              <ReviewAuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/idp-audit-log"
          element={
            <ProtectedRoute>
              <IdpAuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/audit-log"
          element={
            <ProtectedRoute>
              <UserAuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/audit-log"
          element={
            <ProtectedRoute>
              <LeaveAuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/imported-balances"
          element={
            <AdministratorRoute>
              <ImportedLeaveBalancesPage />
            </AdministratorRoute>
          }
        />
        <Route
          path="/disputes"
          element={
            <ProtectedRoute>
              <DisputeManagementPage />
            </ProtectedRoute>
          }
        />
        <Route path="/leave" element={<Navigate to="/team-leave" replace />} />
        <Route
          path="/leave/me"
          element={
            <ProtectedRoute>
              <MyLeavePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team-leave"
          element={
            <ProtectedRoute>
              <TeamLeavePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/organisation"
          element={
            <ProtectedRoute>
              <OrganisationLeavePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/:viewType"
          element={
            <ProtectedRoute>
              <OrganisationWorkforcePage />
            </ProtectedRoute>
          }
        />
        <Route path="/team/workforce" element={<Navigate to="/workforce/team" replace />} />
        <Route path="/organisation/workforce" element={<Navigate to="/workforce/all" replace />} />
        <Route
          path="/individual-development-plan/:employeeId?/:year?"
          element={
            <ProtectedRoute>
              <IndividualDevelopmentPlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/me"
          element={
            <ProtectedRoute>
              <ExpenseFeatureGate>
                <MyExpensesPage />
              </ExpenseFeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/me/new"
          element={
            <ProtectedRoute>
              <ExpenseFeatureGate>
                <NewExpenseClaimPage />
              </ExpenseFeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/team-expenses"
          element={
            <ProtectedRoute>
              <ExpenseFeatureGate>
                <TeamExpensesPage />
              </ExpenseFeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/organisation"
          element={
            <ProtectedRoute>
              <ExpenseFeatureGate>
                <OrganisationExpensesPage />
              </ExpenseFeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/high-fives"
          element={
            <ProtectedRoute>
              <HighFivesFeatureGate>
                <HighFivesPage />
              </HighFivesFeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expense-claims/audit-log"
          element={
            <ProtectedRoute>
              <ExpenseFeatureGate>
                <ExpenseClaimAuditLogPage />
              </ExpenseFeatureGate>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ThemeProvider theme={muiTheme}>
      <AuthProvider>
        <AgentProvider>
          <SuccessSnackbarProvider>
            <SessionExpirySnackbar />
            <AppRoutes />
          </SuccessSnackbarProvider>
        </AgentProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
